/**
 * Stone Investment Project Hero Carousel
 * 
 * Full-width image carousel with Stone Investment's luxury aesthetic:
 * - Cinematic transitions
 * - Elegant navigation
 * - Image counter
 */

'use client';

import { useState, useCallback, useEffect } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

export interface StoneProjectHeroCarouselProps {
  /**
   * Images to display
   */
  images: Array<{
    url: string;
    alt?: string;
    caption?: string;
  }>;
  /**
   * Auto-advance interval in ms (0 to disable)
   */
  autoAdvance?: number;
  /**
   * Enable fullscreen mode on click
   */
  enableFullscreen?: boolean;
  /**
   * Aspect ratio
   */
  aspectRatio?: 'video' | 'cinema' | 'square' | 'portrait';
  /**
   * Custom className
   */
  className?: string;
}

/**
 * StoneProjectHeroCarousel - Full-width image carousel
 */
export function StoneProjectHeroCarousel({
  images,
  autoAdvance = 0,
  enableFullscreen = true,
  aspectRatio = 'cinema',
  className = '',
}: StoneProjectHeroCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const aspectRatioClasses = {
    video: 'aspect-video',
    cinema: 'aspect-[21/9]',
    square: 'aspect-square',
    portrait: 'aspect-[3/4]',
  };
  
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);
  
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);
  
  // Auto-advance
  useEffect(() => {
    if (autoAdvance <= 0 || images.length <= 1) return;
    
    const interval = setInterval(goToNext, autoAdvance);
    return () => clearInterval(interval);
  }, [autoAdvance, goToNext, images.length]);
  
  // Handle keyboard navigation
  useEffect(() => {
    if (!isFullscreen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, goToPrevious, goToNext]);
  
  // Lock body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);
  
  if (images.length === 0) {
    return (
      <div className={`relative ${aspectRatioClasses[aspectRatio]} bg-stone-200 ${className}`}>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-stone-400 font-sans text-sm">No images available</span>
        </div>
      </div>
    );
  }
  
  const currentImage = images[currentIndex];
  
  return (
    <>
      {/* Main Carousel */}
      <div className={`relative overflow-hidden bg-stone-200 ${className}`}>
        <div className={`relative ${aspectRatioClasses[aspectRatio]}`}>
          {images.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-[1500ms] ease-out ${
                index === currentIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <Image
                src={image.url}
                alt={image.alt || `Image ${index + 1}`}
                fill
                className={`object-cover ${enableFullscreen ? 'cursor-pointer' : ''}`}
                priority={index === 0}
                sizes="100vw"
                onClick={() => enableFullscreen && setIsFullscreen(true)}
              />
            </div>
          ))}
        </div>
        
        {/* Navigation Controls */}
        {images.length > 1 && (
          <>
            {/* Previous Button */}
            <button
              onClick={goToPrevious}
              className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/80 backdrop-blur-sm hover:bg-white transition-colors z-10"
              aria-label="Previous image"
            >
              <ChevronLeft size={24} className="text-stone-900" strokeWidth={1} />
            </button>
            
            {/* Next Button */}
            <button
              onClick={goToNext}
              className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/80 backdrop-blur-sm hover:bg-white transition-colors z-10"
              aria-label="Next image"
            >
              <ChevronRight size={24} className="text-stone-900" strokeWidth={1} />
            </button>
            
            {/* Counter */}
            <div className="absolute bottom-6 right-6 px-4 py-2 bg-white/80 backdrop-blur-sm z-10">
              <span className="font-sans text-sm text-stone-900">
                {currentIndex + 1} / {images.length}
              </span>
            </div>
          </>
        )}
        
        {/* Caption */}
        {currentImage?.caption && (
          <div className="absolute bottom-6 left-6 right-24 z-10">
            <p className="font-serif text-white text-lg italic drop-shadow-lg">
              {currentImage.caption}
            </p>
          </div>
        )}
      </div>
      
      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-stone-900">
          {/* Close Button */}
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors z-20"
            aria-label="Close fullscreen"
          >
            <X size={24} className="text-white" strokeWidth={1} />
          </button>
          
          {/* Image */}
          <div className="relative w-full h-full flex items-center justify-center p-12">
            <div className="relative w-full h-full">
              <Image
                src={images[currentIndex]?.url || ''}
                alt={images[currentIndex]?.alt || `Image ${currentIndex + 1}`}
                fill
                className="object-contain"
                sizes="100vw"
              />
            </div>
          </div>
          
          {/* Navigation */}
          {images.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft size={32} className="text-white" strokeWidth={1} />
              </button>
              
              <button
                onClick={goToNext}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="Next image"
              >
                <ChevronRight size={32} className="text-white" strokeWidth={1} />
              </button>
              
              {/* Thumbnail Strip */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 max-w-full overflow-x-auto px-6">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`relative w-20 h-14 flex-shrink-0 overflow-hidden transition-opacity ${
                      index === currentIndex ? 'opacity-100 ring-2 ring-white' : 'opacity-50 hover:opacity-75'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  >
                    <Image
                      src={image.url}
                      alt=""
                      fill
                      className="object-cover"
                      sizes="80px"
                    />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default StoneProjectHeroCarousel;
